import React from 'react';
import { Link } from 'react-router';
import SEO from '../components/SEO';
import GlowCard from '../components/GlowCard';
import { trackEvent } from '../components/analytics';

const AIAssistantPage: React.FC = () => {
  return (
    <div className="animate-fade-in-up">
      <SEO
        title="FlyMate Aviation Assistant for ICAO, AIM, eAIP, SMS, and PANS-OPS"
        description="AviaGov's aviation AI assistant supports ICAO-grounded compliance work across SMS, LMS, eAIP, AIM, AIS, and PANS-OPS while keeping operational decisions under human responsibility."
        ogType="product"
      />

      <section className="py-24 bg-slate-950 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <nav className="flex items-center space-x-2 text-slate-500 text-sm mb-6 uppercase tracking-widest">
              <span>Modules</span>
              <span>/</span>
              <span className="text-accent">FlyMate</span>
            </nav>
            <h1 className="text-5xl lg:text-7xl font-bold font-heading mb-8 leading-tight">
              Aviation AI <br />
              <span className="text-accent">for regulated operations.</span>
            </h1>
            <p className="text-xl text-slate-400 mb-10 leading-relaxed">
              FlyMate helps aviation teams work with ICAO, AIM, AIS, eAIP, SMS, LMS, PANS-OPS, flight planning and operational evidence without treating AI as an operational authority. It is built for grounded answers, drafting assistance, and human-reviewed workflows.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/flymate" className="px-8 py-4 bg-accent text-primary font-bold rounded-xl">
                Open FlyMate Aviation AI
              </Link>
              <Link to="/contact" className="px-8 py-4 border border-white/10 text-white font-bold rounded-xl hover:bg-white/5" onClick={() => trackEvent('demo_request', { form_id: 'ai_assistant_book_demo', module: 'ai_assistant' })}>
                Book Compliance Demo
              </Link>
              <Link to="/modules/sms" className="px-8 py-4 border border-white/10 text-white font-bold rounded-xl hover:bg-white/5">
                See SMS in Action
              </Link>
            </div>
          </div>
          <div className="bg-surface border border-white/10 rounded-[2rem] p-10">
            <div className="text-xs text-accent font-bold uppercase tracking-widest mb-4">FlyMate aviation assistant</div>
            <h2 className="text-2xl font-bold text-white mb-4">ICAO-grounded compliance support layer</h2>
            <p className="text-slate-400 text-sm leading-relaxed">
              Supports aviation compliance teams with source-backed answers, controlled drafting, document preparation, and regulatory interpretation across AviaGov workflows. FlyMate is designed to assist ANSPs, CAAs, AIM teams, AIS teams, operators, and aviation training organizations.
            </p>
          </div>
        </div>
      </section>

      <section className="py-20 px-4 border-b border-white/5 bg-primary">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl mb-12">
            <div className="text-xs text-accent font-bold uppercase tracking-widest mb-4">Aviation AI use cases</div>
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">AI support for ICAO-heavy aviation workflows</h2>
            <p className="text-slate-400 leading-relaxed">
              Generic AI tools are not enough for regulated aviation work. AviaGov focuses aviation AI on grounded research, evidence-aware drafting, workflow support, and human validation for teams working with ICAO references, aeronautical information, safety governance, training evidence, and publication control.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="border border-white/10 bg-white/[0.03] p-6 rounded-xl">
              <h3 className="text-xl font-bold text-white mb-3">AI for ANSPs and CAAs</h3>
              <p className="text-sm leading-6 text-slate-400">
                FlyMate supports civil aviation authorities and ANSP teams that need fast, traceable help with ICAO material, AIM questions, eAIP publication context, and aviation compliance language.
              </p>
            </div>
            <div className="border border-white/10 bg-white/[0.03] p-6 rounded-xl">
              <h3 className="text-xl font-bold text-white mb-3">AI for AIM and AIS teams</h3>
              <p className="text-sm leading-6 text-slate-400">
                Use aviation AI to review publication wording, explain Annex 15 concepts, prepare controlled drafts, and support eAIP workflows while keeping source ownership and approval with the responsible organization.
              </p>
            </div>
            <div className="border border-white/10 bg-white/[0.03] p-6 rounded-xl">
              <h3 className="text-xl font-bold text-white mb-3">AI for aviation compliance</h3>
              <p className="text-sm leading-6 text-slate-400">
                Connect SMS findings, LMS evidence, PANS-OPS documentation, and compliance interpretation into one governed support layer instead of using disconnected generic chat tools.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 px-4">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8">
          <GlowCard>
            <h3 className="text-2xl font-bold text-white mb-4">FlyMate for SMS</h3>
            <ul className="text-slate-400 text-sm space-y-3">
              <li>Helps draft hazard descriptions</li>
              <li>Suggests risk classifications (user validates)</li>
              <li>Assists with corrective action wording</li>
              <li>Explains ICAO Annex 19 concepts in context</li>
            </ul>
          </GlowCard>
          <GlowCard>
            <h3 className="text-2xl font-bold text-white mb-4">FlyMate for LMS</h3>
            <ul className="text-slate-400 text-sm space-y-3">
              <li>Helps create training content</li>
              <li>Maps training to SMS findings</li>
              <li>Assists with compliance wording for audits</li>
              <li>Supports instructors, not replaces them</li>
            </ul>
          </GlowCard>
          <GlowCard>
            <h3 className="text-2xl font-bold text-white mb-4">FlyMate for AIM and eAIP</h3>
            <ul className="text-slate-400 text-sm space-y-3">
              <li>Assists with structured content creation</li>
              <li>Explains Annex 15 requirements</li>
              <li>Supports consistency checks</li>
              <li>Reduces manual effort, not authority control</li>
            </ul>
          </GlowCard>
          <GlowCard>
            <h3 className="text-2xl font-bold text-white mb-4">FlyMate for PANS-OPS</h3>
            <ul className="text-slate-400 text-sm space-y-3">
              <li>Documentation support</li>
              <li>Procedure description drafting</li>
              <li>Compliance explanation (Doc 8168)</li>
              <li>No automatic procedure design decisions</li>
            </ul>
          </GlowCard>
        </div>
      </section>

      <section className="py-20 px-4 bg-slate-950 border-t border-white/5">
        <div className="max-w-5xl mx-auto">
          <div className="text-xs text-accent font-bold uppercase tracking-widest mb-4">Aviation AI FAQ</div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-10">Questions aviation teams ask before using AI</h2>
          <div className="space-y-5">
            {[
              {
                question: 'What is an aviation AI assistant?',
                answer:
                  'An aviation AI assistant is a controlled support tool for aviation-specific research, drafting, interpretation, and workflow assistance. FlyMate focuses on ICAO, AIM, AIS, eAIP, SMS, LMS, PANS-OPS, weather-message, and compliance contexts.',
              },
              {
                question: 'Can AI make operational aviation decisions?',
                answer:
                  'No. AviaGov positions AI as a support layer. Operational decisions, approvals, procedure validation, publication release, and regulatory responsibility remain with qualified humans and approved organizational processes.',
              },
              {
                question: 'Why use FlyMate instead of a generic AI chatbot?',
                answer:
                  'FlyMate is built around aviation terminology, ICAO-heavy workflows, source-backed answers, and refusal behavior when evidence is insufficient. That makes it more appropriate for regulated aviation teams than a generic chatbot.',
              },
            ].map((item) => (
              <div key={item.question} className="border border-white/10 bg-white/[0.03] p-6 rounded-xl">
                <h3 className="text-lg font-bold text-white mb-2">{item.question}</h3>
                <p className="text-sm leading-6 text-slate-400">{item.answer}</p>
              </div>
            ))}
          </div>
          <div className="mt-10">
            <Link to="/flymate" className="inline-flex px-8 py-4 bg-accent text-primary font-bold rounded-xl">
              Try FlyMate aviation AI assistant
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AIAssistantPage;
